import { SvgIcon, type SvgIconProps } from "@mui/material";

import type {
  Icon as PhosphorIcon,
  IconWeight,
} from "@phosphor-icons/react/dist/lib/types";

export type AppIconProps = Omit<SvgIconProps, "component"> & {
  weight?: IconWeight;
};

export type AppIconComponent = (props: AppIconProps) => React.ReactElement;

/**
 * Wraps a Phosphor icon as a real MUI SvgIcon, so sx, color="primary",
 * fontSize="small", data-* attributes and refs all behave exactly like
 * @mui/icons-material icons (1em box, 1.5rem default font-size).
 *
 * inheritViewBox is load-bearing: Phosphor draws on a 256x256 grid, not
 * MUI's 24x24 — without it every glyph renders clipped.
 *
 * defaultWeight is baked in per icon at the mapping site (src/icons/index.ts)
 * so visual parity decisions live in one place, never at call sites.
 *
 * The default data-testid mirrors @mui/icons-material, which stamps every
 * icon with its name outside production — existing getByTestId("CancelIcon")
 * queries keep working. An explicit data-testid prop still wins.
 */
export function createPhosphorMuiIcon(
  Phosphor: PhosphorIcon,
  displayName: string,
  defaultWeight: IconWeight = "regular",
): AppIconComponent {
  function AppIcon({ weight = defaultWeight, ...props }: AppIconProps) {
    return (
      <SvgIcon
        component={Phosphor}
        inheritViewBox
        weight={weight}
        data-testid={import.meta.env.PROD ? undefined : displayName}
        {...props}
      />
    );
  }
  AppIcon.displayName = displayName;
  return AppIcon;
}
